import m from 'mithril'
import {ref, arrayRef, valueRef, isRef, Ref} from './ref'

export interface Filter<T> {
	add(value: T): this
	clear(): void
	delete(value: T): boolean
	has(value: T): boolean
	toggle(value: T): boolean
	readonly size: number
	map: typeof Array.prototype.map
	forEach: typeof Array.prototype.forEach
	filter: typeof Array.prototype.filter
}

abstract class FilterBase<T> implements Filter<T> {
	abstract add(value: T): this
	abstract clear(): void
	abstract delete(value: T): boolean
	abstract has(value: T): boolean
	abstract readonly size: number
	abstract toArray(): ReadonlyArray<T>
	toggle(value: T) {
		if (this.delete(value)) return false
		return !!this.add(value)
	}
	get length(): number {
		return this.size
	}
	map<U>(callbackfn: (value: T, index: number, array: T[]) => U): U[] {
		return this.toArray().map(callbackfn)
	}
	forEach(callbackfn) {
		return this.toArray().forEach(callbackfn)
	}
	filter(callbackfn) {
		return this.toArray().filter(callbackfn)
	}
}

export class FilterSingle<T> extends FilterBase<T> {
	ref: Ref<T>
	constructor(value?: T | Ref<T>) {
		super()
		this.ref = isRef(value) ? value : valueRef(value)
	}
	add(value: T) {
		this.ref(value)
		return this
	}
	clear() {
		this.ref(undefined)
	}
	delete(value: T) {
		if (this.has(value)) return this.clear(), true
		return false
	}
	has(value: T) {
		return this.ref() === value
	}
	get size(): number {
		return this.ref() ? 1 : 0
	}
	toArray() {
		return [this.ref()]
	}
	valueOf() {
		return this.ref()
	}
}

export class FilterMultiple<T> extends FilterBase<T> {
	ref: Ref<ReadonlyArray<T>>
	constructor(values?: Ref<ReadonlyArray<T>> | ReadonlyArray<T> | null) {
		super()
		this.ref = isRef(values) ? values : arrayRef(values)
	}
	add(value: T): this {
		const values = this.ref()
		if (values.indexOf(value) === -1) this.ref(values.concat(value))
		return this
	}
	clear() {
		this.ref([])
	}
	delete(value: T) {
		const values = this.ref()
		const index = values.indexOf(value)
		if (index === -1) return false
		this.ref([...values.slice(0, index), ...values.slice(index + 1)])
		return true
	}
	has(value: T) {
		return this.ref().indexOf(value) > -1
	}
	get size(): number {
		return this.ref().length
	}
	toArray() {
		return this.ref()
	}
	valueOf() {
		return this.ref()
	}
}

const getUrlParameters = () => {
	const query = window.location.search
	return query && query[0] === '?' && query.length > 1
		? m.parseQueryString(query.substr(1))
		: {}
}

const getUrlParameter = (name: string) => getUrlParameters()[name]

const setUrlParameter = (name: string, value: ParameterStorable) => {
	const {pathname, hash} = window.location
	const {[name]: _, ...params} = getUrlParameters()
	const search = m.buildQueryString(value ? {...params, [name]: value} : params)
	const query = search ? '?' + decodeURI(search) : ''
	const path = [pathname, query, hash].join('')
	history.replaceState(history.state, '', path)
	return value
}

type ParameterStorable = string | {toString(): string}
export type ParameterDecode<T> = (value: string) => T

const identity = <T>(v: T): T => v

export const parameterSingle = <T>(
	name: string,
	decode: ParameterDecode<T> = identity as ParameterDecode<T>
) =>
	ref(
		() => decode(getUrlParameter(name)),
		(value: T) => setUrlParameter(name, value)
	)

export const parameterMultiple = <T>(
	name: string,
	decode: ParameterDecode<T> = identity as ParameterDecode<T>
) =>
	ref(
		() => {
			const params = getUrlParameter(name)
			if (!params) return []
			return [].concat(params).map(decode)
		},
		value => name && setUrlParameter(name, value)
	)

export class FilterParameterSingle<
	T extends ParameterStorable = string
> extends FilterSingle<T> {
	constructor(
		name: string,
		decode: ParameterDecode<T> = identity as ParameterDecode<T>
	) {
		super(parameterSingle(name, decode))
	}
}

export class FilterParameterMultiple<
	T extends ParameterStorable = string
> extends FilterMultiple<T> {
	constructor(
		name: string,
		decode: ParameterDecode<T> = identity as ParameterDecode<T>
	) {
		super(parameterMultiple(name, decode))
	}
}

export const exportFilters: (filters: {
	[name: string]: Filter<any>
}) => any = filters => {
	const res = {}
	Object.entries(filters).forEach(
		([name, filter]) => (res[name] = filter.valueOf())
	)
	return res
}
/*
export type FilterType<T> =
	| {store: 'query', name: string, multiple?: boolean}
	| {value: T}
	| {store: null, multiple: true, values: ReadonlyArray<T>}

const construct: <T>({
	
}) => Filter<T> = type => {
	switch (type.store) {
		case 'query':
			if (type.multiple) return new FilterParameterMultiple(type.name)
			return new FilterParameterSingle(type.name)
		default:
			if (type.multiple) return new FilterMultiple(type.values)
			return new FilterSingle(type.value)
	}
}
*/
